// Source           :   https://github.com/stefangabos/Zebra_Datepicker
// Documentation    :   https://stefangabos.github.io/Zebra_Datepicker/metallic.html

import * as $ from "jquery";
import * as ko from "knockout";
import * as moment from "moment";
import * as localisationHelper from "localisationHelper";

// Types
import Moment = moment.Moment;

// Helpers
import "dateTimePicker";

// Models
import { TimePickerBindingConfiguration, TimePickerLibraryOptions } from "Content/ts/Models/Shared/DateTime/timePickerConfiguration";
import { DatePickerBindingConfiguration, DatePickerLibraryOptions } from "Content/ts/Models/Shared/DateTime/datePickerConfiguration";

// Services
import { FontAwesomeServices } from "Content/ts/Shared/KnockoutBindings/Services/fontAwesomeServices";
import { DatePickerServices } from "Content/ts/Shared/KnockoutBindings/DateTime/Services/datePickerServices";
import { TimePickerServices } from "Content/ts/Shared/KnockoutBindings/DateTime/Services/timePickerServices";
import { DateTimePickerServices } from "Content/ts/Shared/Controls/DateTime/Services/dateTimePickerServices";
import { DateTimeFormatterService } from "Content/ts/Shared/Controls/DateTime/Services/dateTimeFormatterService";

export const Deps = {
    LocalisationHelper: localisationHelper
};

export module DateTimeBindings {
    const ZebraDateFormat = DateTimeFormatterService.RetrieveZebraDateFormat();
    const ZebraTimeFormat = DateTimeFormatterService.RetrieveZebraTimeFormat();
    const LocalisedMomentDateFormat = DateTimeFormatterService.LocalisedMomentShortDateFormat;
    const LocalisedMomentTimeFormat = DateTimeFormatterService.LocalisedMomentTimeFormat;
    const OpenDatePickerText = Deps.LocalisationHelper.getString("GenericResources", "ScreenReaderClickToOpenDatePicker");
    const OpenTimePickerText = Deps.LocalisationHelper.getString("GenericResources", "ScreenReaderClickToOpenTimePicker");

    ko.bindingHandlers.datepicker = {
        init: (ele: any, valueAccessor: () => KnockoutComputed<Moment>, allBindingsAccessor: () => DatePickerBindingConfiguration) => {
            let value = valueAccessor(),
                element = (<any>$(ele));
            const allBindings = allBindingsAccessor(),
                configuration = allBindings.configuration;

            if (value() != null && value() != undefined && !value().isValid()) {
                throw new Error("Invalid moment passed to 'datepicker' binding during initialsation");
            }

            var startDate = allBindings.configuration.StartDate != null
                ? allBindings.configuration.StartDate.toDate()
                : null;

            element.Zebra_DatePicker({
                strict: true,
                format: ZebraDateFormat,
                months: moment.localeData().months(),
                months_abbr: moment.localeData().monthsShort(),
                days: moment.localeData().weekdays(),
                days_abbr: moment.localeData().weekdaysShort(),
                readonly_element: false,
                default_position: "above",
                start_date: startDate,
                view: allBindings.configuration.View || "days",
                show_clear_date: configuration ? configuration.AllowClear : true,
                direction: DatePickerServices.ConfigurePickerDirection(allBindings),
                lang_clear_date: Deps.LocalisationHelper.getString("GenericResources", "Clear"),
                show_select_today: allBindings.configuration.ShowToday != null ? allBindings.configuration.ShowToday : Deps.LocalisationHelper.getString("GenericResources", "Today"),
                onSelect: (newValue: string, libraryValue: string, jQueryDate: string) => {
                    value(moment(jQueryDate));
                },
                onClear: () => {
                    value(null);
                }
            } as DatePickerLibraryOptions);

            if (!allBindings.configuration.TabToCalendarButton) {
                //sets the tab index to -1 so the keyboard doesnt tab to it. accessibility requirement 
                $(".Zebra_DatePicker_Icon").attr('tabindex', -1);
            }

            DatePickerServices.DatePickerOnLoad(element, allBindings, value);
            DatePickerServices.ConfigureKeyPressEvents(element, allBindings, value);
            DateTimePickerServices.ConfigureSharedDateTimeMarkup(element, value, FontAwesomeServices.CalendarIcon(), OpenDatePickerText, LocalisedMomentDateFormat);
        }
    };

    ko.bindingHandlers.timepicker = {
        init: (ele: any, valueAccessor: () => KnockoutComputed<Moment>, allBindingsAccessor: () => TimePickerBindingConfiguration) => {
            let value = valueAccessor(),
                element = (<any>$(ele));
            const allBindings = allBindingsAccessor(),
                configuration = allBindings.configuration;

            if (value() != null && value() != undefined && !value().isValid()) {
                throw new Error("Invalid moment passed to 'timepicker' binding during initialsation")
            }

            element.Zebra_DatePicker({
                format: ZebraTimeFormat,
                readonly_element: false,
                default_position: "above",
                show_select_today: false,
                show_clear_date: configuration ? configuration.AllowClear : true,
                enabled_minutes: TimePickerServices.ConfigureEnabledMinutes(configuration),
                lang_clear_date: Deps.LocalisationHelper.getString("GenericResources", "Clear"),
                onSelect: (newValue: string, libraryValue: string, jQueryDate: string) => {
                    value(moment(jQueryDate));
                },
                onClear: () => {
                    value(null);
                }
            } as TimePickerLibraryOptions);

            TimePickerServices.TimePickerOnLoad(element, value);
            TimePickerServices.ConfigureKeyPressEvents(element, value);
            DateTimePickerServices.ConfigureSharedDateTimeMarkup(element, value, FontAwesomeServices.ClockIcon(), OpenTimePickerText, LocalisedMomentTimeFormat);
        }
    };
}